import { TGetListingProductsResponseDTO } from "@/app/api/listing/[slug]/products/listing-products.dto";
import { countryISOs } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { GlobeIcon, LayoutGridIcon, SearchIcon } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";

function getCountryIso(location: string) {
	return countryISOs?.[location as keyof typeof countryISOs];
}

type ProductsTabSearchAndFilterProps = {
	// slug: string;
	initialResults: TGetListingProductsResponseDTO;
	setResults: React.Dispatch<React.SetStateAction<TGetListingProductsResponseDTO>>;
	defaultRegion?: string;
	showGridView?: boolean;
	setShowGridView?: React.Dispatch<React.SetStateAction<boolean>>;
};

const ProductsTabSearchAndFilter = ({
	// slug,
	initialResults,
	setResults,
	defaultRegion,
	showGridView,
	setShowGridView,
}: ProductsTabSearchAndFilterProps) => {
	const regions = [
		{
			displayName: "India",
			countryName: "India",
		},
		{
			displayName: "UAE",
			countryName: "United Arab Emirates",
		},
		// {
		// 	displayName: "USA",
		// 	countryName: "United States",
		// },
		// {
		// 	// Show Europe instead of UK for fractional-investment listing page
		// 	displayName: slug === "fractional-investment" ? "Europe" : "UK",
		// 	countryName: "United Kingdom",
		// },
		// {
		// 	displayName: "Canada",
		// 	countryName: "Canada",
		// },
	];

	const searchParams = useSearchParams();
	const country = searchParams.get("country") || defaultRegion;

	const searchRef = useRef<HTMLInputElement | null>(null);
	const [selectedRegion, setSelectedRegion] = useState<string>(country || "global");

	const handleRegionFilter = useCallback(
		(region: string) => {
			if (region === "global") {
				setSelectedRegion(region);
				setResults(initialResults);

				return;
			}

			const filteredResults = initialResults.filter(
				(result) =>
					result?.region?.toLowerCase() === region.toLowerCase() ||
					result?.hqLocation?.toLowerCase() === region.toLowerCase()
			);

			setSelectedRegion(region);

			if (filteredResults.length) {
				setResults(filteredResults);
			} else {
				setResults(initialResults);
			}
		},
		[initialResults, setResults]
	);

	const handleSearch = (query: string) => {
		if (query === "") {
			setResults(initialResults);
			return;
		}

		const filteredResults = initialResults.filter((result) =>
			result.name.toLowerCase().includes(query.toLowerCase())
		);

		setResults(filteredResults);
	};

	useEffect(() => {
		if (country) {
			handleRegionFilter(country);
		}
	}, [country, handleRegionFilter]);

	return (
		<div className="flex flex-col gap-8 rounded-lg bg-white px-8 py-4 text-sm shadow-sm lg:flex-row lg:items-center">
			<div className="grid grid-cols-3 gap-2 lg:flex">
				<button
					className={cn([
						"flex items-center gap-2 rounded-lg border p-2",
						"global" === selectedRegion ? "border-2 border-brand-primary" : "border-gray-200",
					])}
					onClick={() => handleRegionFilter("global")}
				>
					<GlobeIcon className="h-6 w-6 text-brand-primary" />

					<span>{"Global"}</span>
				</button>
				{regions.map((region) => (
					<button
						key={region.countryName}
						onClick={() => handleRegionFilter(region.displayName)}
						className={cn([
							"flex items-center gap-2 rounded-lg border p-2",
							region.displayName === selectedRegion
								? "border-2 border-brand-primary"
								: "border-gray-200",
						])}
					>
						<span
							className={cn(
								"h-full rounded-sm",
								`fi fi-${getCountryIso(region.countryName)?.toLowerCase()}`
							)}
						></span>

						<span>{region.displayName}</span>
					</button>
				))}
			</div>

			<div className="w-full">
				<div
					className={cn(
						"group flex w-full flex-1 items-center rounded border border-gray-200 bg-white px-4 py-2",
						"transition-colors duration-150 ease-linear focus-within:border-brand-primary"
					)}
				>
					<span>
						<SearchIcon className="h-4 w-4 text-gray-400 transition-colors duration-150 ease-linear group-focus-within:text-blue-500" />
					</span>

					<input
						ref={searchRef}
						id="search"
						type="search"
						name="search"
						placeholder="Search"
						className="w-full px-2 text-sm focus:outline-none"
						onChange={(e) => handleSearch(e.target.value)}
					/>
				</div>
			</div>

			<button
				className={cn([
					"flex items-center gap-2 rounded-lg border p-2",
					showGridView ? "border-blue-500 text-blue-600" : "border-gray-200 text-gray-600",
				])}
				onClick={() => {
					if (setShowGridView !== undefined) {
						setShowGridView(!showGridView);
					}
				}}
			>
				<LayoutGridIcon className="h-6 w-6" />
			</button>
		</div>
	);
};

export default ProductsTabSearchAndFilter;
